(() => {
  'use strict';

  const pad = (value) => String(value).padStart(2, '0');
  const toValue = (date) => `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
  const fromValue = (value) => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(value || '')) return null;
    const date = new Date(`${value}T12:00:00`);
    return Number.isNaN(date.getTime()) ? null : date;
  };
  const isWorkingDay = (date) => date.getDay() >= 1 && date.getDay() <= 5;
  const isSunday = (date) => date.getDay() === 0;

  const earliestDate = (base = new Date()) => {
    const date = new Date(base.getFullYear(), base.getMonth(), base.getDate(), 12);
    let completedWorkingDays = 0;
    while (completedWorkingDays < 2) {
      date.setDate(date.getDate() + 1);
      if (isWorkingDay(date)) completedWorkingDays += 1;
    }
    date.setDate(date.getDate() + 1);
    if (isSunday(date)) date.setDate(date.getDate() + 1);
    return date;
  };

  const validateValue = (value, base = new Date()) => {
    const date = fromValue(value);
    if (!date) return { valid: false, reason: 'empty', message: 'Kies een geldige leverdatum.' };
    if (isSunday(date)) return { valid: false, reason: 'sunday', message: 'Bezorgen op zondag is niet mogelijk. Kies een andere dag.' };
    const minimum = earliestDate(base);
    if (toValue(date) < toValue(minimum)) {
      return {
        valid: false,
        reason: 'too_soon',
        message: `Er moeten minimaal twee volledige werkdagen tussen zitten. De eerst mogelijke datum is ${minimum.toLocaleDateString('nl-NL', { weekday: 'long', day: 'numeric', month: 'long' })}.`
      };
    }
    return { valid: true, reason: '', message: '', date, isSaturday: date.getDay() === 6, weekday: date.toLocaleDateString('nl-NL', { weekday: 'long' }) };
  };

  const isDeliveryInput = (input) => {
    if (!(input instanceof HTMLInputElement) || input.type !== 'date') return false;
    if (input.matches('[data-sc-delivery-date],[data-cart-delivery-date]')) return true;
    const container = input.closest('[class*="flare" i],[id*="flare" i],[class*="fleur" i],[id*="fleur" i]');
    const identity = `${input.name} ${input.id} ${input.className} ${container?.className || ''} ${container?.id || ''}`;
    return /delivery.?date|leverdatum|bezorgdatum|(flare|fleur).*?(date|datum)|(date|datum).*?(flare|fleur)/i.test(identity);
  };

  const getStatus = (input) => {
    const existing = input.closest('label,div')?.querySelector('[data-sc-delivery-output],[data-cart-date-status],.sc-delivery-rule-error');
    if (existing) return existing;
    const status = document.createElement('small');
    status.className = 'sc-delivery-rule-error';
    status.hidden = true;
    status.setAttribute('role', 'alert');
    input.insertAdjacentElement('afterend', status);
    return status;
  };

  const renderValidation = (input, result, options = {}) => {
    const status = getStatus(input);
    input.setCustomValidity(result.valid ? '' : result.message);
    if (result.valid) input.removeAttribute('aria-invalid');
    else input.setAttribute('aria-invalid', 'true');
    input.dataset.scDeliveryInvalid = result.valid ? 'false' : 'true';
    if (!result.valid && options.clearInvalid !== false && input.value) input.value = '';
    if (status) {
      if (result.valid) {
        if (status.classList.contains('sc-delivery-rule-error')) status.hidden = true;
      } else {
        status.textContent = result.message;
        status.hidden = false;
      }
    }
    return result;
  };

  const validate = (input, options = {}) => {
    if (!isDeliveryInput(input)) return { valid: true, ignored: true };
    if (!input.value) {
      input.setCustomValidity('');
      input.removeAttribute('aria-invalid');
      input.dataset.scDeliveryInvalid = 'false';
      return { valid: true, empty: true };
    }
    return renderValidation(input, validateValue(input.value), options);
  };

  const configure = (input) => {
    if (!isDeliveryInput(input)) return;
    input.min = toValue(earliestDate());
    input.step = '1';
    input.dataset.scDeliveryRules = 'active';
    validate(input, { clearInvalid: true });
  };

  const configureAll = (root = document) => root.querySelectorAll('input[type="date"]').forEach(configure);
  const validateAll = () => {
    const inputs = [...document.querySelectorAll('input[type="date"]')].filter(isDeliveryInput);
    const invalid = inputs.find((input) => !validate(input, { clearInvalid: true }).valid);
    if (invalid) {
      invalid.focus();
      invalid.reportValidity();
      return false;
    }
    return true;
  };

  window.StudioCosyDeliveryRules = { earliestDate, earliestValue: () => toValue(earliestDate()), validateValue, validate, validateAll, configureAll, isDeliveryInput };

  const initialise = () => {
    configureAll();
    document.addEventListener('input', (event) => {
      if (isDeliveryInput(event.target)) validate(event.target, { clearInvalid: false });
    }, true);
    document.addEventListener('change', (event) => {
      if (!isDeliveryInput(event.target)) return;
      const result = validate(event.target, { clearInvalid: true });
      if (!result.valid) {
        event.preventDefault();
        event.stopImmediatePropagation();
        event.target.focus();
      }
    }, true);
    document.addEventListener('submit', (event) => {
      const form = event.target;
      if (form.querySelector?.('[name="checkout"]') && !validateAll()) event.preventDefault();
    }, true);

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => mutation.addedNodes.forEach((node) => {
        if (!(node instanceof Element)) return;
        if (node.matches('input[type="date"]')) configure(node);
        configureAll(node);
      }));
    });
    observer.observe(document.body, { childList: true, subtree: true });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialise, { once: true });
  else initialise();
})();
